/**
 * Skeleton components for Standings page
 * MUST match exact dimensions of actual components to prevent CLS
 */

// Static skeleton (no animation) to reduce jank
function StaticSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`bg-muted/50 rounded ${className}`} aria-hidden="true" />
  );
}

// Animated skeleton for first element only
function AnimatedSkeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-muted/60 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

function PodiumCardSkeleton({
  size,
  animated = false,
}: {
  size: "lg" | "md" | "sm";
  animated?: boolean;
}) {
  const Skel = animated ? AnimatedSkeleton : StaticSkeleton;

  const sizeClasses = {
    lg: "p-6",
    md: "p-5",
    sm: "p-4",
  };

  return (
    <div
      className={`rounded-xl border-2 border-border bg-muted/10 ${sizeClasses[size]}`}
    >
      {/* Position Badge */}
      <Skel className="h-10 w-16 rounded-lg mb-4" />

      {/* Name */}
      <Skel className="h-6 w-32 mb-1" />

      {/* Team */}
      <Skel className="h-4 w-24 mb-4" />

      {/* Points */}
      <div className="mt-auto">
        <Skel className="h-8 w-20 mb-1" />
        <Skel className="h-3 w-12" />
      </div>
    </div>
  );
}

export function PodiumSkeleton() {
  return (
    <div className="mb-8">
      <StaticSkeleton className="h-8 w-24 mx-auto mb-6" />

      {/* 2-1-3 Podium Layout */}
      <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto items-end">
        {/* P2 - Second Place */}
        <div className="flex flex-col justify-end">
          <PodiumCardSkeleton size="md" />
        </div>

        {/* P1 - First Place (animated) */}
        <div className="flex flex-col justify-end">
          <PodiumCardSkeleton size="lg" animated />
        </div>

        {/* P3 - Third Place */}
        <div className="flex flex-col justify-end">
          <PodiumCardSkeleton size="sm" />
        </div>
      </div>
    </div>
  );
}

export function TableSkeleton({
  rows = 17,
  showTeamColumn = true,
}: {
  rows?: number;
  showTeamColumn?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="text-left px-4 py-3 w-16">
              <StaticSkeleton className="h-4 w-8" />
            </th>
            <th className="text-left px-4 py-3">
              <StaticSkeleton className="h-4 w-20" />
            </th>
            {showTeamColumn && (
              <th className="text-left px-4 py-3">
                <StaticSkeleton className="h-4 w-16" />
              </th>
            )}
            <th className="text-right px-4 py-3 w-24">
              <StaticSkeleton className="h-4 w-12 ml-auto" />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="border-b border-border/50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <StaticSkeleton className="w-1 h-6" />
                  <StaticSkeleton className="h-5 w-6" />
                </div>
              </td>
              <td className="px-4 py-3">
                <StaticSkeleton className="h-5 w-32" />
              </td>
              {showTeamColumn && (
                <td className="px-4 py-3">
                  <StaticSkeleton className="h-4 w-24" />
                </td>
              )}
              <td className="px-4 py-3 text-right">
                <StaticSkeleton className="h-6 w-12 ml-auto" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StandingsSkeleton({
  showTeamColumn = true,
}: {
  showTeamColumn?: boolean;
}) {
  return (
    <>
      <PodiumSkeleton />
      <TableSkeleton rows={17} showTeamColumn={showTeamColumn} />
    </>
  );
}
